import { COUNTRY_CODE, CURRENCY_CODE } from "@/lib/constant";
import { useSnackbarContext } from "@repo/contexts/SnackbarContext";
import {
  applePayMerchantValidate,
  beginCheckout,
  createOrderCookie,
  placeOrder,
  processPayment,
} from "@repo/server-actions/checkout";
import { useLocale, useTranslations } from "next-intl";
import { useRouter } from "next/navigation";
import { useCallback } from "react";

const usePayByApplePay = () => {
  const locale = useLocale();
  const { setSnackbar } = useSnackbarContext();
  const t = useTranslations("Common");
  const router = useRouter();

  const payByApplePay = useCallback(
    (cartTotal, payLoad, selectedPaymentMethod) => {
      // save the last selected payment method in localstorage to be used on order status page
      localStorage.setItem("paymentMethod", selectedPaymentMethod);
      const { total } = cartTotal;
      const paymentRequest = {
        countryCode: COUNTRY_CODE,
        currencyCode: CURRENCY_CODE,
        supportedNetworks: ["visa", "masterCard", "amex"],
        total: {
          label: "Alhome",
          amount: total,
        },
        merchantIdentifier: "merchant.com.alhome.ollkom",
        merchantCapabilities: ["supports3DS"],
      };

      const session = new ApplePaySession(6, paymentRequest);

      session.onvalidatemerchant = async (event) => {
        console.log("onvalidatemerchant", event);
        const domainName = window.location.hostname; // get the domain from window object
        const validationReqBody = {
          validationUrl: event.validationURL,
          domainName, // Pickup this value dynamically from the host or environment
        };
        const validateResponse = await applePayMerchantValidate(
          validationReqBody,
          locale
        );
        if (validateResponse) {
          session.completeMerchantValidation(validateResponse);
        } else {
          console.error("Merchant validation failed", validateResponse);
          session.abort();
        }
      };

      session.onpaymentauthorized = async (event) => {
        console.log("onpaymentauthorized", event);
        const appleData = event?.payment?.token;
        if (!appleData) {
          console.error("No Apple Pay token received");
          session.completePayment(ApplePaySession.STATUS_FAILURE);
          return;
        }
        const domainName = window.location.hostname; // get the domain from window object
        try {
          // Begin Checkout Process
          const beginOrderProcess = await beginCheckout(locale);
          if (!beginOrderProcess.success) {
            setSnackbar({
              open: true,
              message: t("Something_Went_Wrong_Please_Try_Again"),
              type: "error",
            });
            session.abort();
            return;
          }
          // Place order in OMS to get the order ID for further processing
          const order = await placeOrder(
            payLoad,
            cartTotal,
            selectedPaymentMethod,
            locale
          );
          // Dislpay snackbar if placeOrder is failing
          if (!order?.orderId) {
            setSnackbar({
              open: true,
              message:
                order?.message || t("Something_Went_Wrong_Please_Try_Again"),
              type: "error",
            });
            session.abort();
            return;
          }
          console.log("Order placed successfully", order);
          // Created the order cookie
          await createOrderCookie(order?.orderId);
          // Process Payment to get the Auth ID and Transaction ID
          const processPay = await processPayment(
            selectedPaymentMethod,
            locale,
            "",
            "",
            appleData
          );
          // Dislpay snackbar if process payment is failing
          if (!processPay?.data) {
            setSnackbar({
              open: true,
              message:
                processPay?.message ||
                t("Something_Went_Wrong_Please_Try_Again"),
              type: "error",
            });
            session.completePayment(ApplePaySession.STATUS_FAILURE);
            return;
          }
          console.log("processPay", processPay?.data);
          const transactionData = {
            authorizationId: "",
            transactionId: processPay?.data?.transactionId || "",
          };
          sessionStorage.setItem(
            "transactionData",
            JSON.stringify(transactionData)
          );
          session?.completePayment(ApplePaySession.STATUS_SUCCESS);
          // redirect to order success / failed page
          router.push(`https://${domainName}/en/order-status`);
          //
          //performAuthorization(token, session);
        } catch (error) {
          console.error("Apple Pay error:", error);
          setSnackbar({
            open: true,
            message: t("Something_Went_Wrong_Please_Try_Again"),
            type: "error",
          });
          session?.completePayment(ApplePaySession.STATUS_FAILURE);
        }
      };

      // Session cancellation - Need to be used for anything to be destroyed or removed
      session.oncancel = async (event) => {
        console.log("oncancel", event);
      };
      session.begin();
    },
    [locale, setSnackbar, t, router]
  );

  return {
    payByApplePay,
  };
};

export default usePayByApplePay;
